import React, { useState, useRef, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import toast from "react-hot-toast";
import api, { getFreshAccessToken } from "../../../../../chat/api";
import MemoizedMarkdown from "../../../MemoizedMarkdown";
import { AIAvatarIcon } from "../../icons";
import FeedbackPopup from "../../../../FeedbackPopup";
import { emitPlanLimit } from "../../../../../../events/planLimitEvents";

const WS_BASE_URL =
  process.env.REACT_APP_WS_URL || "wss://stagingbackend.docusensa.com";
const MAX_RECONNECT_ATTEMPTS = 3;
const RECONNECT_DELAY = 3000;

// Human labels for the backend's tool_starting frames, shown while the agent
// is inside a tool call (otherwise the user just sees silent typing dots).
const TOOL_ACTIVITY_LABELS = {
  search_web: "🔍 Searching the web…",
  search_rfp_project_documents: "Searching the RFP documents…",
  search_company_knowledge_base: "Searching your knowledge base…",
  get_proposal_structure: "Reading the proposal structure…",
  get_section_content: "Reading the section…",
  draft_section_with_rag: "Drafting the section…",
  add_section: "Updating the proposal structure…",
  bulk_add_sections: "Updating the proposal structure…",
  remove_section: "Updating the proposal structure…",
  update_section_content: "Saving the section…",
};

const BotAvatar = ({ pulse = false }) => (
  <div
    className={`w-8 h-8 rounded-full border border-[#E6E6E6] flex items-center justify-center flex-shrink-0 ${
      pulse ? "animate-pulse" : ""
    }`}
  >
    <AIAvatarIcon />
  </div>
);

const SendIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
  <path d="M0.0875054 2.67789C-0.381426 1.08386 1.11237 -0.409707 2.70771 0.102466C5.73058 1.07294 8.61558 2.43401 11.2906 4.15158C11.3651 4.19945 11.4529 4.25275 11.5487 4.31089C11.8483 4.49276 12.2261 4.72208 12.5214 4.98142C12.9535 5.3608 13.3338 5.89706 13.3338 6.66815C13.3338 7.43924 12.9535 7.9755 12.5214 8.35488C12.2261 8.61421 11.8483 8.84353 11.5487 9.0254C11.4529 9.08354 11.3651 9.13684 11.2906 9.18471C8.61558 10.9023 5.73058 12.2634 2.70771 13.2338C1.11237 13.746 -0.381426 12.2524 0.087505 10.6584L0.924472 7.81332C1.00793 7.52961 1.26831 7.3348 1.56404 7.3348H5.22377C5.59196 7.3348 5.89043 7.03632 5.89043 6.66813C5.89043 6.29994 5.59196 6.00146 5.22377 6.00146H1.56403C1.26831 6.00146 1.00792 5.80665 0.924464 5.52295L0.0875054 2.67789Z" fill="white"/>
</svg>
);

// Shown in place of the send button while the Copilot is responding.
const StopCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 30 30" fill="none">
    <rect width="30" height="30" rx="15" fill="#A04A97" />
    <rect x="8" y="8" width="14" height="14" rx="2" fill="white" />
  </svg>
);

const ThumbUpIcon = ({ filled = false }) =>
  filled ? (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <path d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z" />
    </svg>
  ) : (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="13" viewBox="0 0 14 13" fill="none">
      <path d="M12.1282 4.09089C12.4453 4.09089 12.7254 4.2125 12.9685 4.45573C13.2117 4.69884 13.3333 4.97895 13.3333 5.29606V6.37289C13.3333 6.44289 13.3243 6.51789 13.3063 6.59789C13.2884 6.67778 13.2684 6.75239 13.2462 6.82173L11.3355 11.3301C11.2401 11.5433 11.0799 11.7234 10.8552 11.8704C10.6304 12.0174 10.3958 12.0909 10.1515 12.0909H3.141V4.09089L7.01917 0.24606C7.15172 0.113615 7.30517 0.0345602 7.4795 0.00889349C7.65383 -0.0167732 7.82089 0.0131158 7.98067 0.0985602C8.14056 0.184116 8.25767 0.305504 8.332 0.462727C8.40633 0.619949 8.42217 0.782782 8.3795 0.951227L7.6615 4.09089H12.1282ZM4.141 4.51656V11.0909H10.1538C10.2008 11.0909 10.2489 11.0781 10.298 11.0524C10.3472 11.0267 10.3847 10.984 10.4103 10.9242L12.3333 6.42423V5.29606C12.3333 5.23617 12.3141 5.187 12.2757 5.14856C12.2372 5.11012 12.1881 5.09089 12.1282 5.09089H6.3975L7.23333 1.43706L4.141 4.51656ZM1.20517 12.0909C0.873722 12.0909 0.59 11.9729 0.354 11.7369C0.118 11.5009 0 11.2172 0 10.8857V5.29606C0 4.96462 0.118 4.68089 0.354 4.44489C0.59 4.20889 0.873722 4.09089 1.20517 4.09089H3.141V5.09089H1.20517C1.14528 5.09089 1.09611 5.11012 1.05767 5.14856C1.01922 5.187 1 5.23617 1 5.29606V10.8857C1 10.9456 1.01922 10.9948 1.05767 11.0332C1.09611 11.0717 1.14528 11.0909 1.20517 11.0909H3.141V12.0909H1.20517Z" fill="currentColor"/>
    </svg>
  );

const ThumbDownIcon = ({ filled = false }) =>
  filled ? (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <path d="M15 3H6c-.83 0-1.54.5-1.84 1.22l-3.02 7.05c-.09.23-.14.47-.14.73v2c0 1.1.9 2 2 2h6.31l-.95 4.57-.03.32c0 .41.17.79.44 1.06L9.83 23l6.59-6.59c.36-.36.58-.86.58-1.41V5c0-1.1-.9-2-2-2zm4 0v12h4V3h-4z" />
    </svg>
  ) : (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="13" viewBox="0 0 14 13" fill="none">
      <path d="M1.20517 8C0.888055 8 0.607944 7.87839 0.364833 7.63517C0.121611 7.39206 0 7.11195 0 6.79483V5.718C0 5.648 0.00899998 5.573 0.027 5.493C0.0448889 5.41311 0.0649445 5.3385 0.0871667 5.26917L1.99783 0.760834C2.09328 0.547612 2.25339 0.3675 2.47817 0.2205C2.70294 0.0734997 2.9375 0 3.18183 0H10.1923V8L6.31417 11.8448C6.18161 11.9773 6.02817 12.0563 5.85383 12.082C5.6795 12.1077 5.51244 12.0778 5.35267 11.9923C5.19278 11.9068 5.07567 11.7854 5.00133 11.6282C4.927 11.4709 4.91117 11.3081 4.95383 11.1397L5.67183 8H1.20517ZM9.19233 7.57433V1H3.1795C3.1325 1 3.08444 1.01283 3.03533 1.0385C2.98611 1.06417 2.94867 1.10689 2.923 1.16667L1 5.66667V6.79483C1 6.85472 1.01922 6.90389 1.05767 6.94233C1.09611 6.98078 1.14528 7 1.20517 7H6.93583L6.1 10.6538L9.19233 7.57433ZM12.1282 0C12.4596 0 12.7433 0.118 12.9793 0.354C13.2153 0.59 13.3333 0.873722 13.3333 1.20517V6.79483C13.3333 7.12628 13.2153 7.41 12.9793 7.646C12.7433 7.882 12.4596 8 12.1282 8H10.1923V7H12.1282C12.1881 7 12.2372 6.98078 12.2757 6.94233C12.3141 6.90389 12.3333 6.85472 12.3333 6.79483V1.20517C12.3333 1.14528 12.3141 1.09611 12.2757 1.05767C12.2372 1.01922 12.1881 1 12.1282 1H10.1923V0H12.1282Z" fill="currentColor"/>
    </svg>
  );

const CopyIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="11" height="13" viewBox="0 0 11 13" fill="none">
  <path d="M3.76917 10.1025C3.4325 10.1025 3.1475 9.98583 2.91417 9.7525C2.68083 9.51917 2.56417 9.23417 2.56417 8.8975V1.20517C2.56417 0.868389 2.68083 0.583333 2.91417 0.35C3.1475 0.116667 3.4325 0 3.76917 0H9.4615C9.79828 0 10.0833 0.116667 10.3167 0.35C10.55 0.583333 10.6667 0.868389 10.6667 1.20517V8.8975C10.6667 9.23417 10.55 9.51917 10.3167 9.7525C10.0833 9.98583 9.79828 10.1025 9.4615 10.1025H3.76917ZM3.76917 9.1025H9.4615C9.51283 9.1025 9.55983 9.08117 9.6025 9.0385C9.64528 8.99572 9.66667 8.94872 9.66667 8.8975V1.20517C9.66667 1.15383 9.64528 1.10683 9.6025 1.06417C9.55983 1.02139 9.51283 1 9.4615 1H3.76917C3.71794 1 3.67094 1.02139 3.62817 1.06417C3.5855 1.10683 3.56417 1.15383 3.56417 1.20517V8.8975C3.56417 8.94872 3.5855 8.99572 3.62817 9.0385C3.67094 9.08117 3.71794 9.1025 3.76917 9.1025ZM0.5385 3.23067C0.385944 3.23067 0.258055 3.17911 0.154833 3.076C0.051611 2.97278 0 2.84489 0 2.69233C0 2.53978 0.051611 2.41189 0.154833 2.30867C0.258055 2.20544 0.385944 2.15383 0.5385 2.15383C0.691055 2.15383 0.818944 2.20544 0.922167 2.30867C1.02539 2.41189 1.077 2.53978 1.077 2.69233C1.077 2.84489 1.02539 2.97278 0.922167 3.076C0.818944 3.17911 0.691055 3.23067 0.5385 3.23067ZM0.5385 5.58967C0.385944 5.58967 0.258055 5.53806 0.154833 5.43483C0.051611 5.33172 0 5.20389 0 5.05133C0 4.89878 0.051611 4.77089 0.154833 4.66767C0.258055 4.56444 0.385944 4.51283 0.5385 4.51283C0.691055 4.51283 0.818944 4.56444 0.922167 4.66767C1.02539 4.77089 1.077 4.89878 1.077 5.05133C1.077 5.20389 1.02539 5.33172 0.922167 5.43483C0.818944 5.53806 0.691055 5.58967 0.5385 5.58967ZM0.5385 7.94867C0.385944 7.94867 0.258055 7.89706 0.154833 7.79383C0.051611 7.69061 0 7.56272 0 7.41017C0 7.25761 0.051611 7.12978 0.154833 7.02667C0.258055 6.92344 0.385944 6.87183 0.5385 6.87183C0.691055 6.87183 0.818944 6.92344 0.922167 7.02667C1.02539 7.12978 1.077 7.25761 1.077 7.41017C1.077 7.56272 1.02539 7.69061 0.922167 7.79383C0.818944 7.89706 0.691055 7.94867 0.5385 7.94867ZM0.5385 10.3077C0.385944 10.3077 0.258055 10.2561 0.154833 10.1528C0.051611 10.0496 0 9.92172 0 9.76917C0 9.61661 0.051611 9.48872 0.154833 9.3855C0.258055 9.28239 0.385944 9.23083 0.5385 9.23083C0.691055 9.23083 0.818944 9.28239 0.922167 9.3855C1.02539 9.48872 1.077 9.61661 1.077 9.76917C1.077 9.92172 1.02539 10.0496 0.922167 10.1528C0.818944 10.2561 0.691055 10.3077 0.5385 10.3077ZM0.5385 12.6667C0.385944 12.6667 0.258055 12.6151 0.154833 12.5118C0.051611 12.4086 0 12.2807 0 12.1282C0 11.9756 0.051611 11.8477 0.154833 11.7445C0.258055 11.6413 0.385944 11.5897 0.5385 11.5897C0.691055 11.5897 0.818944 11.6413 0.922167 11.7445C1.02539 11.8477 1.077 11.9756 1.077 12.1282C1.077 12.2807 1.02539 12.4086 0.922167 12.5118C0.818944 12.6151 0.691055 12.6667 0.5385 12.6667ZM2.8975 12.6667C2.74494 12.6667 2.61706 12.6151 2.51383 12.5118C2.41061 12.4086 2.359 12.2807 2.359 12.1282C2.359 11.9756 2.41061 11.8477 2.51383 11.7445C2.61706 11.6413 2.74494 11.5897 2.8975 11.5897C3.05006 11.5897 3.17794 11.6413 3.28117 11.7445C3.38428 11.8477 3.43583 11.9756 3.43583 12.1282C3.43583 12.2807 3.38428 12.4086 3.28117 12.5118C3.17794 12.6151 3.05006 12.6667 2.8975 12.6667ZM5.2565 12.6667C5.10394 12.6667 4.97606 12.6151 4.87283 12.5118C4.76961 12.4086 4.718 12.2807 4.718 12.1282C4.718 11.9756 4.76961 11.8477 4.87283 11.7445C4.97606 11.6413 5.10394 11.5897 5.2565 11.5897C5.40906 11.5897 5.53689 11.6413 5.64 11.7445C5.74322 11.8477 5.79483 11.9756 5.79483 12.1282C5.79483 12.2807 5.74322 12.4086 5.64 12.5118C5.53689 12.6151 5.40906 12.6667 5.2565 12.6667ZM7.61533 12.6667C7.46278 12.6667 7.33494 12.6151 7.23183 12.5118C7.12861 12.4086 7.077 12.2807 7.077 12.1282C7.077 11.9756 7.12861 11.8477 7.23183 11.7445C7.33494 11.6413 7.46278 11.5897 7.61533 11.5897C7.76789 11.5897 7.89578 11.6413 7.999 11.7445C8.10222 11.8477 8.15383 11.9756 8.15383 12.1282C8.15383 12.2807 8.10222 12.4086 7.999 12.5118C7.89578 12.6151 7.76789 12.6667 7.61533 12.6667Z" fill="#52525B"/>
</svg>
);

const DoubleCheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
    <path d="M2 13l4 4 8-9" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M11 16l1 1 8-9" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const TypingDots = () => (
  <div className="flex items-center gap-1.5 py-1">
    <style>{`
      @keyframes copilotDot {
        0%, 70%, 100% { transform: scale(0.55); opacity: 0.45; }
        35% { transform: scale(1); opacity: 1; }
      }
    `}</style>
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="w-2.5 h-2.5 rounded-full bg-[#9B4D96]"
        style={{
          animation: "copilotDot 1.2s ease-in-out infinite",
          animationDelay: `${i * 0.18}s`,
        }}
      />
    ))}
  </div>
);

const DEFAULT_SUGGESTIONS = [
  "Suggest USPs from my knowledge base",
  "What is this RFP about?",
  "Summarize this RFP for me",
];

// Fresh client-side session id. The proposal websocket accepts any chat_id and
// persists messages against it (chat_module="proposal_copilot" + folder_id), so
// there's no need to pre-create a row server-side — and crucially this avoids the
// new-chat endpoint's 600s cache that would hand back a stale, already-used id.
const generateChatId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

// Map a persisted history row (GET /copilot/get-chat-sessions/<chat_id>/) onto the
// in-memory message shape. is_client=true ⇒ user; bot rows keep message_id/run_id
// so the thumbs-up/down feedback controls work on reloaded conversations too.
const mapHistoryItem = (h) =>
  h.is_client
    ? { role: "user", content: h.message }
    : {
        role: "assistant",
        content: h.message,
        messageId: h.bot_message_id || null,
        runId: h.run_id || null,
      };

const PlusIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <path d="M12 5v14M5 12h14" strokeLinecap="round" />
  </svg>
);

// First name for the greeting; falls back to "there".
const getUserFirstName = () => {
  const full =
    localStorage.getItem("fullName") ||
    localStorage.getItem("first_name") ||
    localStorage.getItem("username") ||
    "";
  return full.trim().split(" ")[0] || "there";
};

const ProposalCopilot = ({
  userName,
  suggestions = DEFAULT_SUGGESTIONS,
  isActive = true,
  onRefreshProposal,
  // Called with (statusKey, targetId) when the agent starts a RAG draft, so
  // the editor can follow the live stream like a normal generate/regenerate.
  onDraftStreaming,
  // Authoritative folder id from the proposal page; falls back to the URL below.
  folderId: folderIdProp = null,
  // Section/subsection open in the editor; sent with each message so the agent
  // scopes edits. Goes out as `subsection_id` or `section_id`.
  activeSectionId = null,
  activeSectionIsSubsection = false,
  // Lifted to parent so the conversation + session persist across panel reopen.
  messages = [],
  setMessages,
  chatId = null,
  setChatId,
}) => {
  const displayName = userName || getUserFirstName();

  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState("");
  // Name of the tool the agent is currently executing (null when idle).
  const [activeTool, setActiveTool] = useState(null);
  const [isChatInitializing, setIsChatInitializing] = useState(false);
  const [token, setToken] = useState(null);
  const [folderId, setFolderId] = useState(folderIdProp || null);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [pendingApproval, setPendingApproval] = useState(false);
  const [activeFeedbackPopup, setActiveFeedbackPopup] = useState({
    isOpen: false,
    messageId: null,
    type: "down",
  });

  const location = useLocation();
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const messageQueueRef = useRef([]);
  const streamingMessageRef = useRef("");
  const isConnectingRef = useRef(false);
  const isMountedRef = useRef(true);
  const hasInitializedChatRef = useRef(false);
  const shouldRefreshRef = useRef(false);
  // { messageId, runId } from "final" frame, stamped onto the message on "completed".
  const pendingMetaRef = useRef(null);

  // Auto-scroll to the latest content.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages, streamingMessage, isLoading, isStreaming, pendingApproval]);

  // Auto-grow the composer up to a max, then let it scroll.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
    el.style.overflowY = el.scrollHeight > 120 ? "auto" : "hidden";
  }, [input]);

  // Prefer the folderId passed from the proposal page; otherwise derive it from
  // the URL (/rfp-projects/project/<id>/...).
  useEffect(() => {
    if (folderIdProp) {
      setFolderId(folderIdProp);
      return;
    }
    const parts = location.pathname.split("/");
    const i = parts.indexOf("project");
    if (i !== -1 && parts[i + 1]) setFolderId(parts[i + 1]);
  }, [folderIdProp, location.pathname]);

  // Read access token from cookie on mount; clean up on unmount.
  useEffect(() => {
    isMountedRef.current = true;
    const cookieToken =
      Cookies.get("access_token") || localStorage.getItem("access_token") || null;
    if (cookieToken) setToken(cookieToken);
    else toast.error("Authentication token not found");
    return () => {
      isMountedRef.current = false;
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
      if (wsRef.current) {
        wsRef.current.close(1000, "Component unmounting");
        wsRef.current = null;
      }
    };
  }, []);

  // Close the socket when the tab is not the active one.
  useEffect(() => {
    if (!isActive && wsRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
      wsRef.current.close(1000, "Tab switched");
      wsRef.current = null;
      isConnectingRef.current = false;
    }
  }, [isActive]);

  // On first open, continue this proposal's existing Copilot conversation if one
  // exists (so history is visible and persists across the Setup→Export steps),
  // otherwise start a fresh session. Runs once per mount — once chatId is set it
  // is held in the parent and survives step switches without reloading.
  useEffect(() => {
    if (!token || !isActive || !folderId || hasInitializedChatRef.current || chatId)
      return;
    hasInitializedChatRef.current = true;
    const initializeChat = async () => {
      setIsChatInitializing(true);
      try {
        // 1. Most-recent folder-scoped (proposal_copilot) session, if any.
        let existingChatId = null;
        try {
          const listRes = await api.get(
            `/api/v1/rfp/kb-copilot/chats/?folder_id=${folderId}`
          );
          const sessions = listRes.data?.chat_sessions || [];
          if (sessions.length > 0) existingChatId = sessions[0].chat_id;
        } catch (error) {
          // 404 simply means this proposal has no chat history yet.
          if (error.response?.status !== 404)
            console.error("Copilot session list error:", error);
        }

        // 2. Load its history and resume the same chat_id.
        if (existingChatId) {
          try {
            const histRes = await api.get(
              `/api/v1/copilot/get-chat-sessions/${existingChatId}/`
            );
            const history = histRes.data?.chat_history || [];
            if (isMountedRef.current) setMessages(history.map(mapHistoryItem));
          } catch (error) {
            // Resume the session even if the history fetch fails/empties.
            if (error.response?.status !== 404)
              console.error("Copilot history load error:", error);
          }
          if (isMountedRef.current) setChatId(existingChatId);
          return;
        }

        // 3. No prior conversation → fresh session.
        if (isMountedRef.current) setChatId(generateChatId());
      } catch (error) {
        console.error("Copilot chat init error:", error);
        hasInitializedChatRef.current = false;
        toast.error("Failed to start Copilot. Please try again.");
      } finally {
        if (isMountedRef.current) setIsChatInitializing(false);
      }
    };
    initializeChat();
  }, [token, isActive, folderId, chatId, setChatId, setMessages]);

  // "New chat" — tear down the current socket and start a clean session.
  const handleNewChat = useCallback(() => {
    if (isChatInitializing) return;
    clearTimeout(reconnectTimeoutRef.current);
    reconnectTimeoutRef.current = null;
    if (wsRef.current) {
      wsRef.current.close(1000, "New chat");
      wsRef.current = null;
    }
    isConnectingRef.current = false;
    reconnectAttemptsRef.current = 0;
    messageQueueRef.current = [];
    streamingMessageRef.current = "";
    pendingMetaRef.current = null;
    setStreamingMessage("");
    setIsStreaming(false);
    setIsLoading(false);
    setPendingApproval(false);
    setMessages([]);
    setChatId(generateChatId());
  }, [isChatInitializing, setMessages, setChatId]);

  const handleWebSocketMessage = useCallback(
    (data) => {
      if (!isMountedRef.current) return;

      if (data.type === "approval_required") {
        const partial = streamingMessageRef.current;
        if (partial && partial.trim()) {
          setMessages((prev) => [...prev, { role: "assistant", content: partial }]);
        }
        streamingMessageRef.current = "";
        setStreamingMessage("");
        setPendingApproval(true);
        setIsStreaming(false);
        setIsLoading(false);
        return;
      }

      if (data.type === "tool_starting") {
        setActiveTool(data.name || null);
        return;
      }

      if (data.type === "tool_completed") {
        setActiveTool(null);
        return;
      }

      // The agent started a RAG draft — hand its status key to the editor so
      // the section streams in live, same as generate/regenerate.
      if (data.type === "draft_streaming") {
        if (onDraftStreaming && data.status_key && data.target_id) {
          onDraftStreaming(data.status_key, data.target_id);
        }
        return;
      }

      // A tool hit a plan limit; the reply explains it, this opens the upgrade popup.
      if (data.type === "plan_limit") {
        emitPlanLimit(data.message);
        return;
      }

      if (data.type === "info") {
        shouldRefreshRef.current = true;
        setIsStreaming(true);
        streamingMessageRef.current = "";
        setStreamingMessage("");
        return;
      }

      if (data.type === "progress") {
        streamingMessageRef.current += data.message || data.chunk || "";
        setStreamingMessage(streamingMessageRef.current);
        return;
      }

      if (data.type === "final") {
        // Keep run_id + bot_message_id for feedback (thumbs up/down).
        const meta = data.data || data;
        pendingMetaRef.current = {
          messageId: meta.bot_message_id || meta.message_id || null,
          runId: meta.run_id || null,
        };
        return;
      }

      if (data.type === "completed" || data.status === "completed") {
        const finalText = streamingMessageRef.current;
        if (finalText.trim()) {
          const meta = pendingMetaRef.current;
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content: finalText,
              messageId: meta?.messageId || null,
              runId: meta?.runId || null,
            },
          ]);
        }
        pendingMetaRef.current = null;
        if (shouldRefreshRef.current && data.tool_call === true && onRefreshProposal) {
          onRefreshProposal();
        }
        shouldRefreshRef.current = false;
        streamingMessageRef.current = "";
        setStreamingMessage("");
        setActiveTool(null);
        setIsStreaming(false);
        setIsLoading(false);
        return;
      }

      if (data.type === "error" || data.error) {
        if (data.code === "plan_limit") emitPlanLimit(data.message || data.error);
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: data.error || data.message || "An error occurred. Please try again.",
            isError: true,
          },
        ]);
        streamingMessageRef.current = "";
        setStreamingMessage("");
        setActiveTool(null);
        setIsStreaming(false);
        setIsLoading(false);
        shouldRefreshRef.current = false;
        toast.error(data.error || "An error occurred");
      }
    },
    [onRefreshProposal, onDraftStreaming]
  );

  const connectWebSocket = useCallback(async () => {
    if (!isActive || isConnectingRef.current) return;
    if (!folderId || !chatId || !isMountedRef.current) return;
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;
    if (wsRef.current) {
      wsRef.current.close(1000, "Reconnecting");
      wsRef.current = null;
    }

    isConnectingRef.current = true;
    // Reconnect with a freshly validated token — refreshed if it expired while
    // idle (otherwise the socket would 403 and loop).
    let freshToken = null;
    try {
      freshToken = await getFreshAccessToken();
    } catch (e) {
      freshToken = null;
    }
    if (!freshToken || !isMountedRef.current || !isActive) {
      isConnectingRef.current = false;
      return;
    }
    setToken(freshToken);

    try {
      const wsUrl = `${WS_BASE_URL}/ws/rfp-proposal-copilot/${folderId}/${chatId}/?token=${freshToken}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        isConnectingRef.current = false;
        reconnectAttemptsRef.current = 0;
        while (messageQueueRef.current.length > 0) {
          ws.send(JSON.stringify(messageQueueRef.current.shift()));
        }
      };

      ws.onmessage = (event) => {
        try {
          handleWebSocketMessage(JSON.parse(event.data));
        } catch (error) {
          console.error("Error parsing Copilot message:", error);
        }
      };

      ws.onerror = () => {
        isConnectingRef.current = false;
      };

      ws.onclose = (event) => {
        isConnectingRef.current = false;
        wsRef.current = null;
        if (event.code !== 1000 && isMountedRef.current && isActive) {
          reconnectAttemptsRef.current += 1;
          if (reconnectAttemptsRef.current <= MAX_RECONNECT_ATTEMPTS) {
            reconnectTimeoutRef.current = setTimeout(() => {
              if (isMountedRef.current && isActive) connectWebSocket();
            }, RECONNECT_DELAY);
          } else {
            toast.error("Connection lost. Please reopen the Copilot.");
          }
        }
      };
    } catch (error) {
      console.error("Error creating Copilot socket:", error);
      isConnectingRef.current = false;
    }
  }, [folderId, chatId, isActive, handleWebSocketMessage]);

  // Connect once the session is ready. Deferred a tick so StrictMode's throwaway
  // dev mount cancels it before a socket is created (avoids a spurious 1006 + reconnect).
  useEffect(() => {
    let connectTimer = null;
    if (chatId && token && folderId && !isChatInitializing && isActive) {
      connectTimer = setTimeout(() => connectWebSocket(), 60);
    }
    return () => {
      if (connectTimer) clearTimeout(connectTimer);
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    };
  }, [chatId, token, folderId, isChatInitializing, isActive, connectWebSocket]);

  const sendOverSocket = useCallback(
    (payload) => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify(payload));
      } else {
        messageQueueRef.current.push(payload);
        if (!isConnectingRef.current && isActive) connectWebSocket();
      }
    },
    [isActive, connectWebSocket]
  );

  const sendMessage = useCallback(
    (text) => {
      const content = (text || "").trim();
      if (!content || isLoading || isChatInitializing || pendingApproval) return;
      setMessages((prev) => [...prev, { role: "user", content }]);
      setInput("");
      if (textareaRef.current) textareaRef.current.style.height = "auto";
      setIsLoading(true);
      streamingMessageRef.current = "";
      setStreamingMessage("");
      // Fresh reconnect budget for this turn (socket may have died while idle).
      reconnectAttemptsRef.current = 0;
      const payload = { client_message: content };
      if (activeSectionId) {
        payload[activeSectionIsSubsection ? "subsection_id" : "section_id"] =
          activeSectionId;
      }
      sendOverSocket(payload);
    },
    [
      isLoading,
      isChatInitializing,
      pendingApproval,
      sendOverSocket,
      activeSectionId,
      activeSectionIsSubsection,
    ]
  );

  const handleApprovalAction = (action) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      const payload = { action };
      if (activeSectionId) {
        payload[activeSectionIsSubsection ? "subsection_id" : "section_id"] =
          activeSectionId;
      }
      wsRef.current.send(JSON.stringify(payload));
    } else {
      toast.error("Copilot not connected. Please try again.");
      return;
    }
    setMessages((prev) => [
      ...prev,
      { role: "user", content: action === "approve" ? "Approve & run" : "Deny" },
    ]);
    setPendingApproval(false);
    setIsLoading(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  // Toggle thumb and open the feedback popup; FeedbackPopup handles the API call.
  const handleThumb = (index, type) => {
    const msg = messages[index];
    if (!msg || !msg.messageId || !msg.runId) {
      toast.error("Can't record feedback for this message.");
      return;
    }
    const already = msg.feedback === type;
    setMessages((prev) =>
      prev.map((m, i) => (i === index ? { ...m, feedback: already ? null : type } : m))
    );
    if (!already) {
      setActiveFeedbackPopup({ isOpen: true, messageId: msg.messageId, type });
    }
  };

  const closeFeedbackPopup = () =>
    setActiveFeedbackPopup({ isOpen: false, messageId: null, type: "down" });

  const handleCopy = async (content, index) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      toast.error("Failed to copy");
    }
  };

  const composerDisabled = isLoading || isChatInitializing || pendingApproval;
  const isEmpty =
    messages.length === 0 && !isLoading && !isStreaming && !isChatInitializing;

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* New chat button — commented out for now.
      <div className="flex items-center justify-end px-5 pt-2">
        <button
          type="button"
          onClick={handleNewChat}
          disabled={isChatInitializing || isLoading || isStreaming}
          className="flex items-center gap-1 text-[#A04A97] font-outfit text-[13px] font-medium hover:opacity-80 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
          aria-label="Start a new chat"
        >
          <PlusIcon /> New chat
        </button>
      </div>
      */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto custom-scrollbar px-5 pt-5 pb-5 space-y-4">
        <div className="flex items-start gap-2.5">
          <BotAvatar pulse={isChatInitializing} />
          <p className="text-[#000000B3] font-outfit text-[14px] font-medium pt-1">
            {isChatInitializing
              ? "Loading…"
              : `Hi ${displayName}! How can I help you today?`}
          </p>
        </div>

        {isEmpty && (
          <div className="space-y-2 pt-1">
            {suggestions.map((s) => (
              <button
                key={s}
                onClick={() => sendMessage(s)}
                disabled={composerDisabled}
                className="w-full text-left px-4 py-2 rounded-xl border border-[#E7C9E3] text-[#A04A97] font-outfit text-[14px] font-medium hover:bg-[#C95EBE0D] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {messages.map((m, i) =>
          m.role === "user" ? (
            <div key={i} className="flex justify-end">
              <div className="max-w-[85%] bg-[#F0F0F0] text-[#000000B3] font-normal font-outfit text-[14px] leading-6 rounded-2xl rounded-tr-sm px-4 py-2.5 whitespace-pre-wrap">
                {m.content}
              </div>
            </div>
          ) : (
            <div key={i} className="flex items-start gap-2.5">
              <BotAvatar />
              <div className="max-w-[85%]">
                <div
                  className={`text-[14px] font-outfit leading-6 ${
                    m.isError ? "text-[#E21919]" : "text-[#18101C]"
                  }`}
                >
                  <MemoizedMarkdown content={m.content} />
                </div>
                <div
                  id={`feedback-btns-${m.messageId || i}`}
                  className="relative flex items-center gap-2 mt-2 text-[#9CA3AF] w-fit"
                >
                  {!m.isError && m.messageId && m.runId && (
                    <>
                      <button
                        onClick={() => handleThumb(i, "up")}
                        className={`transition-colors ${
                          m.feedback === "up"
                            ? "text-[#16A34A]"
                            : "text-[#52525B] hover:text-[#18101C]"
                        }`}
                        aria-label="Helpful"
                        aria-pressed={m.feedback === "up"}
                      >
                        <ThumbUpIcon filled={m.feedback === "up"} />
                      </button>
                      <button
                        onClick={() => handleThumb(i, "down")}
                        className={`transition-colors ${
                          m.feedback === "down"
                            ? "text-[#E21919]"
                            : "text-[#52525B] hover:text-[#18101C]"
                        }`}
                        aria-label="Not helpful"
                        aria-pressed={m.feedback === "down"}
                      >
                        <ThumbDownIcon filled={m.feedback === "down"} />
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => handleCopy(m.content, i)}
                    className={`transition-colors ${
                      copiedIndex === i ? "text-[#16A34A]" : "hover:text-[#18101C]"
                    }`}
                    aria-label={copiedIndex === i ? "Copied" : "Copy"}
                  >
                    {copiedIndex === i ? <DoubleCheckIcon /> : <CopyIcon />}
                  </button>

                  {!m.isError && m.messageId && m.runId && (
                    <FeedbackPopup
                      isOpen={
                        activeFeedbackPopup.isOpen &&
                        activeFeedbackPopup.messageId === m.messageId
                      }
                      onClose={closeFeedbackPopup}
                      messageId={m.messageId || i}
                      feedbackType={activeFeedbackPopup.type}
                      messageMeta={{
                        django_message_id: m.messageId,
                        run_id: m.runId,
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          )
        )}

        {(isLoading || isStreaming) && (
          <div className={`flex gap-2.5 ${streamingMessage ? "items-start" : "items-center"}`}>
            <BotAvatar pulse />
            <div className="max-w-[85%]">
              {streamingMessage ? (
                <div className="text-[#18101C] text-[14px] font-outfit leading-6">
                  <MemoizedMarkdown content={streamingMessage} />
                </div>
              ) : activeTool ? (
                <div className="text-[#6B7280] font-outfit text-[13px] animate-pulse">
                  {TOOL_ACTIVITY_LABELS[activeTool] || "Working…"}
                </div>
              ) : (
                <TypingDots />
              )}
            </div>
          </div>
        )}

        {pendingApproval && (
          <div className="flex gap-2.5">
            <button
              onClick={() => handleApprovalAction("deny")}
              className="px-4 py-2 rounded-lg border border-[#D4D4D8] bg-white text-[#52525B] font-outfit font-medium text-[14px] hover:bg-[#FAFAFA] hover:border-[#BD3A3A] hover:text-[#BD3A3A] transition-colors"
            >
              Deny
            </button>
            <button
              onClick={() => handleApprovalAction("approve")}
              className="flex-1 py-2 rounded-lg bg-[#279643] hover:bg-[#1F7A36] text-white font-outfit font-semibold text-[14px] transition-colors shadow-sm"
            >
              ✓ Approve & run
            </button>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="px-5 pb-5 pt-2">
        <div
          className={`flex items-end gap-2 rounded-[22px] border pl-4 pr-1.5 py-1.5 transition-colors ${
            isLoading || isStreaming
              ? "border-gray-200 bg-[#F3F4F6]"
              : "border-gray-300 focus-within:border-[#A04A97]"
          }`}
        >
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendMessage(input);
              }
            }}
            rows={1}
            disabled={composerDisabled}
            placeholder={
              pendingApproval
                ? "Approval pending…"
                : isChatInitializing
                ? "Loading…"
                : isLoading
                ? "Copilot is responding…"
                : "Ask me anything…"
            }
            className="flex-1 resize-none max-h-[120px] overflow-hidden custom-scrollbar bg-transparent border-0 font-outfit font-medium !text-[14px] leading-[normal] text-[#000000B3] placeholder:text-[#00000080] placeholder:text-[14px] placeholder:font-medium disabled:text-[#00000080] focus:outline-none focus:ring-0 disabled:cursor-not-allowed self-center py-1.5"
          />
          {isLoading || isStreaming ? (
            <span
              aria-label="Copilot is responding"
              className="w-9 h-9 flex-shrink-0 flex items-center justify-center"
            >
              <StopCircleIcon />
            </span>
          ) : (
            <button
              type="submit"
              disabled={!input.trim() || composerDisabled}
              aria-label="Send"
              className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full bg-[#A04A97] text-white transition-colors disabled:bg-[#00000040] disabled:cursor-not-allowed"
            >
              <SendIcon />
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default ProposalCopilot;
